import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileArrowUp,
  FileText,
  DownloadSimple,
  X,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useDirectory } from "@/lib/directory";

const prettySize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

export const FileVault = ({
  entity,
  entityId,
  title = "Documents & Media",
  emailJobId = null,
}) => {
  const { nameOf } = useDirectory();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem("sw_token");

  const emailDoc = async (fileId) => {
    setEmailing(fileId);
    try {
      const { data } = await api.post(
        `/reservations/${emailJobId}/files/${fileId}/email`,
      );
      toast.success(`Document emailed to ${data.to}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Email failed",
      );
    }
    setEmailing(null);
  };

  useEffect(() => {
    api
      .get(`/${entity}/${entityId}/files`)
      .then(({ data }) => setFiles(data.files))
      .catch(() => setFiles([]));
  }, [entity, entityId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/${entity}/${entityId}/files`, fd);
      setFiles(data.files);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (fileId) => {
    const { data } = await api.delete(`/${entity}/${entityId}/files/${fileId}`);
    setFiles(data.files);
    toast.success("File removed");
  };

  return (
    <div
      className="no-print rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid={`${entity}-file-vault`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          {title}
        </div>
        <Button
          data-testid="upload-file-button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="h-10 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
        >
          <FileArrowUp size={16} weight="bold" className="mr-2" />
          {uploading ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          data-testid="file-input"
          type="file"
          onChange={upload}
          className="hidden"
        />
      </div>

      <div className="mt-4 space-y-2" data-testid="file-list">
        {files.length === 0 && (
          <p className="text-sm text-zinc-500">
            No documents yet — upload templates, drawings, invoices or PDFs.
          </p>
        )}
        {files.map((f) => (
          <div
            key={f.id}
            data-testid={`file-row-${f.id}`}
            className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
          >
            <FileText
              size={20}
              weight="bold"
              className="shrink-0 text-yellow-500"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {f.original_filename}
              </div>
              <div className="font-mono-data text-xs text-zinc-500">
                {prettySize(f.size)} · {nameOf(f.uploaded_by)}
              </div>
            </div>
            <a
              href={`${API}/files/${f.storage_path}?auth=${token}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`file-download-${f.id}`}
              className="rounded-md p-2 text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-yellow-400"
            >
              <DownloadSimple size={18} weight="bold" />
            </a>
            {emailJobId && (
              <button
                type="button"
                data-testid={`file-email-${f.id}`}
                onClick={() => emailDoc(f.id)}
                disabled={emailing === f.id}
                title="Email to customer"
                className="rounded-md p-2 text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-yellow-400 disabled:opacity-40"
              >
                <PaperPlaneTilt size={18} weight="bold" />
              </button>
            )}
            <button
              type="button"
              data-testid={`file-remove-${f.id}`}
              onClick={() => remove(f.id)}
              className="rounded-md p-2 text-zinc-500 transition-colors duration-150 hover:bg-red-950/40 hover:text-red-400"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
